'use client';

import { CinemarkLink } from '@/components/cinemark-link/cinemark-link';
import { MovieItemComponent } from '@/components/movie-item/movie-item';
import { ShowtimeListComponent } from '@/components/showtimes/showtime-list/showtime-list';
import { ShowtimeSkeletonComponent } from '@/components/skeletons/showtime-skeleton';
import { Anchor } from '@/components/ui/anchor';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useDataAnalytics } from '@/hooks/analytics/analytics';
import { useMediaQuery } from '@/hooks/use-media-query';
import { MoviePoster } from '@/types/movie-poster';
import { BasicShowtime } from '@/types/showtimes';
import { dataTestTransformer } from '@/utils/data-test-helper';
import { isActiveStatus, isFutureShowtime } from '@/utils/showtime-filters';
import { isMp4, withAutoplay } from '@/utils/video-helper';
import { useState } from 'react';
import { NoShowtimeContentComponent } from './no-showtime-content';
import { PreFirstShowtimeNoShowtimeContentComponent } from './pre-first-showtime-no-showtime-content';
import { PresaleNoShowtimeContentComponent } from './presale-no-showtime-content';
import trailerDialogStyles from './trailer-dialog.module.scss';

const MOBILE_VIEW_MEDIA_QUERY = '(max-width: 767.98px)';

type TrailerDialogComponentProps = {
  children: React.ReactNode;
  closeOnBrowseShowtimes?: boolean;
  dataLinkCategory: string;
  dataLinkName?: string;
  dialogIndex?: number;
  firstShowDate?: Date;
  hasActiveShowtimes?: boolean;
  hasPresaleShowtimes?: boolean;
  hideMessageWithNoShowtimes?: boolean;
  isBeforeFirstShowDate?: boolean;
  isShowtimesLoading?: boolean;
  movieDataLinkName?: string;
  moviePoster: MoviePoster;
  onOpenChange?: (open: boolean) => void;
  preFirstShowtimeMessage?: string;
  presaleLinkText?: string;
  presaleLinkUrl?: string;
  presaleMessage?: string;
  publicOnSaleDate?: Date;
  showtimes?: BasicShowtime[];
  theaterName?: string;
  timeZone?: string;
};

export const TrailerDialogComponent = ({
  children,
  closeOnBrowseShowtimes,
  dataLinkCategory,
  dataLinkName,
  dialogIndex,
  firstShowDate,
  hasActiveShowtimes,
  hasPresaleShowtimes,
  hideMessageWithNoShowtimes,
  isBeforeFirstShowDate,
  isShowtimesLoading,
  moviePoster,
  onOpenChange,
  preFirstShowtimeMessage,
  presaleLinkText,
  presaleLinkUrl,
  presaleMessage,
  publicOnSaleDate,
  showtimes,
  theaterName,
  timeZone }: TrailerDialogComponentProps): React.JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const { trackClick } = useDataAnalytics();
  const isMobileView = useMediaQuery(MOBILE_VIEW_MEDIA_QUERY);
  const safeShowtimes = showtimes ?? [];

  const handleOpenChange = (open: boolean): void => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  const handleBrowseShowtimesClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
    if (closeOnBrowseShowtimes) {
      event.preventDefault();
      handleOpenChange(false);
    }

    trackClick({
      category: dataLinkCategory,
      label: `Movie - ${moviePoster.movieTitle}`,
    });
  };

  const browseShowtimeAnchor = <Anchor className={trailerDialogStyles['browse-showtimes']}
    data-test="link-browse-showtimes-for-movie"
    dataLinkCategory={dataLinkCategory}
    dataLinkName={`Movie - ${moviePoster.movieTitle}`}
    href={moviePoster.movieUrl}
    onClick={handleBrowseShowtimesClick}
  >
    Browse Showtimes
  </Anchor>;

  const upcomingShowtimes = safeShowtimes
    .filter(showtime => isActiveStatus(showtime.status) && isFutureShowtime(showtime, { offsetMinutes: 0 }))
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
    .slice(0, isMobileView ? 6 : 5);

  const hasDisplayedShowtimes = upcomingShowtimes.length > 0;
  const showPresaleFooter = !hasDisplayedShowtimes && !!hasPresaleShowtimes && !!presaleMessage;
  const showPreFirstShowtimeFooter = !hasDisplayedShowtimes && !showPresaleFooter && !!isBeforeFirstShowDate
    && !!preFirstShowtimeMessage && !!firstShowDate;
  const showEmptyFooter = !isShowtimesLoading && !hasDisplayedShowtimes && !hasActiveShowtimes && !showPresaleFooter
    && !showPreFirstShowtimeFooter && !hideMessageWithNoShowtimes;
  const footerClassName = [
    trailerDialogStyles['modal-footer'],
    !hasDisplayedShowtimes && trailerDialogStyles['modal-footer--single'],
    showEmptyFooter && trailerDialogStyles['modal-footer--empty'],
  ].filter(Boolean).join(' ');

  return (
    <Dialog open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <DialogTrigger asChild
        onClick={() => trackClick({
          linkCategory: dataLinkCategory,
          linkName: dataLinkName || `Trailer Click${dialogIndex != null && dialogIndex >= 0 ? ` ${dialogIndex + 1}` : ` ${moviePoster.movieTitle}`}`,
        })}
      >
        {children}
      </DialogTrigger>

      <DialogContent className={trailerDialogStyles['modal-content']}>
        <DialogHeader showCloseButton={false}>
          <DialogTitle className="hide" />
        </DialogHeader>
        <DialogDescription className="hide" />

        <div className="relative w-full aspect-video -mt-4">
          {isMp4(moviePoster.movieTrailerUrl) ?
            <video autoPlay
              controls
              className="absolute top-0 left-0 w-full h-full"
            >
              <source src={moviePoster.movieTrailerUrl}
                type="video/mp4"
              />
            </video>
            :
            <iframe allowFullScreen
              allow="autoplay; encrypted-media"
              className="absolute top-0 left-0 w-full h-full"
              src={withAutoplay(moviePoster.movieTrailerUrl)}
              title={moviePoster.movieTitle}
            />
          }
        </div>

        <DialogFooter className={footerClassName}>
          <div className="flex">
            <div className={trailerDialogStyles['movie-poster']}>
              <div className="flex gap-ms">
                <MovieItemComponent className="max-w-xl"
                  dataLinkCategory={dataLinkCategory}
                  movie={moviePoster}
                />

                <div>
                  <CinemarkLink useAnchor
                    className="h3 max-w-60 md:inline-block"
                    dataLinkCategory={dataLinkCategory}
                    dataLinkName={`Movie - ${moviePoster.movieTitle}`}
                    dataTest={`link-movie-${moviePoster.movieTitle ? dataTestTransformer(moviePoster.movieTitle) : moviePoster.cinemarkMovieId}`}
                    href={moviePoster.movieUrl}
                  >
                    {moviePoster.movieTitle}
                  </CinemarkLink>
                  <CinemarkLink useAnchor
                    className="flex gap-sm"
                    dataLinkCategory={dataLinkCategory}
                    dataLinkName={`Movie - ${moviePoster.movieTitle}`}
                    dataTest={`link-movie-${moviePoster.movieTitle ? dataTestTransformer(moviePoster.movieTitle) : moviePoster.cinemarkMovieId}`}
                    href={moviePoster.movieUrl}
                  >
                    <span className="text-secondary">{moviePoster.movieRating}</span>
                    <span className="text-secondary">{moviePoster.movieRuntime}</span>
                  </CinemarkLink>
                </div>
              </div>
            </div>
          </div>

          {isShowtimesLoading &&
            <div className="flex w-full gap-sm"
              data-test="trailer-showtimes-loading"
            >
              {
                [1, 2, 3, 4, 5].map(key =>
                  <ShowtimeSkeletonComponent key={key} />
                )
              }
            </div>
          }

          {!isShowtimesLoading && hasDisplayedShowtimes &&
            browseShowtimeAnchor
          }

          {!isShowtimesLoading && showPresaleFooter &&
            <PresaleNoShowtimeContentComponent dataLinkCategory={dataLinkCategory}
              dataLinkName={`Movie Details - ${moviePoster.movieTitle}`}
              moviePoster={moviePoster}
              presaleLinkText={presaleLinkText}
              presaleLinkUrl={presaleLinkUrl}
              presaleMessage={presaleMessage!}
              publicOnSaleDate={publicOnSaleDate}
            />
          }

          {!isShowtimesLoading && showPreFirstShowtimeFooter &&
            <PreFirstShowtimeNoShowtimeContentComponent dataLinkCategory={dataLinkCategory}
              dataLinkName={`Movie Details - ${moviePoster.movieTitle}`}
              firstShowDate={firstShowDate!}
              moviePoster={moviePoster}
              preFirstShowtimeMessage={preFirstShowtimeMessage!}
            />
          }

          {showEmptyFooter &&
            <NoShowtimeContentComponent dataLinkCategory={dataLinkCategory}
              dataLinkName={`Movie Details - ${moviePoster.movieTitle}`}
              moviePoster={moviePoster}
            />
          }

          {!isShowtimesLoading && safeShowtimes.length > 0 && hasDisplayedShowtimes && theaterName &&
            <h3 className={trailerDialogStyles['starting-soon']}
              data-test="starting-soon-at-theater"
            >
              Starting soon at {theaterName}
            </h3>
          }

          {!isShowtimesLoading && safeShowtimes.length > 0 && hasDisplayedShowtimes &&
            <ShowtimeListComponent cinemarkMovieId={moviePoster.cinemarkMovieId}
              className={trailerDialogStyles['showtime-list']}
              dataLinkCategory={dataLinkCategory}
              listClassName={trailerDialogStyles['showtime-grid']}
              showtimes={upcomingShowtimes}
              theaterId={moviePoster.theaterId}
              timeZone={timeZone}
            />
          }
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
